// Small hooks behind the minimap canvas: element size, wheel-to-zoom, and the
// keyboard focus model. Split out of useMinimapInteraction.ts to keep that hook
// under the repo's 400-line rule; each piece here is self-contained.

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type PreparedBoard, tileAt } from './minimapBoard.js';
import { axialToUnit } from './minimapGeometry.js';
import { keyAction } from './minimapKeyboard.js';
import { type FocusState, centreTile } from './minimapPointer.js';
import { type CanvasSize, type Viewport, ZOOM_STEP, revealPoint } from './minimapViewport.js';

const REVEAL_MARGIN = 14;

/** Track a container's CSS size (rounded), zero before the first measure. */
export function useElementSize(ref: RefObject<HTMLElement | null>): CanvasSize {
  const [size, setSize] = useState<CanvasSize>({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      setSize((s) => (s.width === w && s.height === h ? s : { width: w, height: h }));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/**
 * Wheel-to-zoom on the canvas. Non-passive, so the page does not scroll while
 * the map zooms; the anchor is canvas-local CSS px.
 */
export function useWheelZoom(
  ref: RefObject<HTMLCanvasElement | null>,
  onZoomAt: (factor: number, anchor: { x: number; y: number }) => void,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const unit = e.deltaMode === 1 ? 16 : 1;
      const factor = Math.exp(-e.deltaY * unit * (e.ctrlKey ? 0.01 : 0.0018));
      onZoomAt(factor, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [ref, onZoomAt]);
}

export interface KeyboardFocusArgs {
  boardRef: RefObject<PreparedBoard>;
  vpRef: RefObject<Viewport | null>;
  sizeRef: RefObject<CanvasSize>;
  setCustom: (vp: Viewport | null) => void;
  clickRef: RefObject<((q: number, r: number) => void) | undefined>;
  zoomBy: (factor: number) => void;
  ownCitiesRef: RefObject<readonly { q: number; r: number }[] | undefined>;
}

export interface KeyboardFocus {
  readonly focus: FocusState | null;
  readonly focused: boolean;
  readonly onKeyDown: (e: ReactKeyboardEvent<HTMLCanvasElement>) => void;
  readonly onFocus: () => void;
  readonly onBlur: () => void;
}

/** Arrows move a focus hex, Enter activates it, +/- zoom, 0 fits, C cycles own cities. */
export function useMinimapKeyboardFocus(args: KeyboardFocusArgs): KeyboardFocus {
  const { boardRef, vpRef, sizeRef, setCustom, clickRef, zoomBy, ownCitiesRef } = args;
  const [focus, setFocus] = useState<FocusState | null>(null);
  const [focused, setFocused] = useState(false);
  const focusRef = useRef<FocusState | null>(null);
  focusRef.current = focus;

  /** Bring a tile into view (pan the viewport if it sits outside the margin). */
  const revealTile = (q: number, r: number) => {
    const vp = vpRef.current;
    if (!vp) return;
    const u = axialToUnit(q, r);
    const revealed = revealPoint(
      vp,
      u.x,
      u.y,
      boardRef.current.bounds,
      sizeRef.current,
      vp.scale + REVEAL_MARGIN,
    );
    if (revealed !== vp) setCustom(revealed);
  };

  const moveFocus = (dq: number, dr: number) => {
    const b = boardRef.current;
    const cur = focusRef.current;
    if (!cur) {
      setFocus(centreTile(b)); // first arrow press lands on the start hex
      return;
    }
    const next = { q: cur.q + dq, r: cur.r + dr };
    if (!tileAt(b, next.q, next.r)) return;
    revealTile(next.q, next.r);
    setFocus(next);
  };

  /**
   * `C`: move the focus to the next of the local player's own cities (spec §5).
   * Presses cycle in the order the App lists them and wrap around; the focus
   * follows the camera, but the camera itself does not move (Enter does that).
   */
  const cycleOwnCity = () => {
    const cities = ownCitiesRef.current;
    if (!cities || cities.length === 0) return;
    const cur = focusRef.current;
    const at = cur ? cities.findIndex((c) => c.q === cur.q && c.r === cur.r) : -1;
    const next = cities[(at + 1) % cities.length];
    if (!next) return;
    revealTile(next.q, next.r);
    setFocus({ q: next.q, r: next.r });
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLCanvasElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const act = keyAction(e.key, e.shiftKey);
    if (act.kind === 'none') return;
    e.preventDefault();
    if (act.kind === 'move') moveFocus(act.dq, act.dr);
    else if (act.kind === 'zoom') zoomBy(act.dir > 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
    else if (act.kind === 'fit') setCustom(null);
    else if (act.kind === 'cycle-city') cycleOwnCity();
    else if (act.kind === 'activate') {
      const f = focusRef.current ?? centreTile(boardRef.current);
      const t = f ? tileAt(boardRef.current, f.q, f.r) : undefined;
      if (f && t && t.visibility !== 'fog') clickRef.current?.(f.q, f.r);
    }
  };

  const onFocus = () => {
    setFocused(true);
    setFocus((cur) => cur ?? centreTile(boardRef.current));
  };
  const onBlur = () => setFocused(false);

  return { focus, focused, onKeyDown, onFocus, onBlur };
}
